<template>
  <v-container>
    <v-row>
      <v-col cols="3">
        <h1>Payroll Settings</h1>
      </v-col>
      <v-col cols="3">
          <v-select
            v-model="selectedBusinessId"
            :items="businesses"
            item-text="name"
            item-value="businessId"
            label="Business"
            @change="updateView"
          ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-snackbar v-model="popup.show" timeout=30000>
        {{popup.message}}
        <template v-slot:action="{ attrs }">
          <v-btn
            :color="popup.colour"
            text
            v-bind="attrs"
            @click="popup.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-row>

    <div v-if="selectedBusinessId !== ''">
      <v-row>
        <v-col cols="6">
          <v-text-field label="Name" v-model="view.businessName"></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-select
            v-model="view.payrollSystem"
            :items="payrollSystems"
            item-text="name"
            item-value="code"
            label="Payroll System"
          ></v-select>
        </v-col>
      </v-row>

      <v-row align="center">
        <v-col cols="5">
          <KronosWFDSettings
            v-if="view.payrollSystem=='kronoswfd'"
            @updateBusiness="updateBusiness"
            @saveCredentials="saveCredentials"
            :credentials="business.payrollCredentials"
            :saving="saving"
          />
          <KeypaySettings
            v-if="view.payrollSystem=='keypay'"
            @updateBusiness="updateBusiness"
            @saveCredentials="saveCredentials"
            :credentials="business.payrollCredentials || {}"
            :saving="saving"
          />
          <XeroSettings
            v-if="view.payrollSystem=='xero'"
            @updateBusiness="updateBusiness"
            @saveCredentials="saveCredentials"
            @getOptions="getOptions"
            :credentials="view.payrollCredentials"
            :saving="saving"
            :businessId="selectedBusinessId"
            :payrollOptions="payrollOptions"
            :loadingOptions="loadingOptions"
          />
          <KSCSettings
            v-if="view.payrollSystem=='ksc'"
            @updateBusiness="updateBusiness"
            @saveCredentials="saveCredentials"
            :credentials="business.payrollCredentials"
          />
          <NimbusT2WSettings
            v-if="view.payrollSystem=='nimbusT2W'"
            @updateBusiness="updateBusiness"
            @saveCredentials="saveCredentials"
            :credentials="business.payrollCredentials"
          />

        </v-col>
        <v-btn value="Submit" @click="updateBusiness" color="primary">Save</v-btn>
      </v-row>

    </div>
  </v-container>
</template>

<script>
import BusinessService from '../services/BusinessService';
import UserService from '../services/UserService';
import KronosWFDSettings from '../components/payroll/KronosWFD.vue';
import KeypaySettings from '../components/payroll/KeyPay.vue';
import KSCSettings from '../components/payroll/KSC.vue';
import NimbusT2WSettings from '../components/payroll/NimbusT2W.vue';
import XeroSettings from '../components/payroll/Xero.vue';

export default {
  name: 'BusinessSettings',
  data() {
    return {
      view: {
        businessName: '',
        payrollSystem: '',
      },
      businesses: [],
      payrollSystems: [
        { name: 'None', code: '' },
        { name: 'Kronos Workforce Dimensions', code: 'kronoswfd' },
        { name: 'KeyPay', code: 'keypay' },
        { name: 'King Pay', code: 'ksc' },
        { name: 'Nimbus Time2Work', code: 'nimbusT2W' },
        { name: 'Xero', code: 'xero' },
        { name: 'MYOB', code: 'myob' },
      ],
      payrollOptions: {},
      popup: {
        show: false,
        message: '',
        colour: 'blue',
      },
      selectedBusinessId: '',
      user: {},
      userId: this.$auth.user.sub.split('|')[1],
      saving: false,
      loadingOptions: false,
    };
  },
  async created() {
    const token = await this.$auth.getTokenSilently();
    this.user = await UserService.getUser(this.userId, token);
    const { businessIds } = this.user;
    if (businessIds.length > 0) {
      [this.selectedBusinessId] = businessIds;
    }
    (businessIds || []).forEach((businessId) => {
      this.getBusiness(businessId);
    });
  },
  methods: {
    updateView() {
      this.view.businessName = this.business.name;
      this.view.payrollSystem = this.business.payrollSystem;
      this.view.payrollCredentials = this.business.payrollCredentials;
    },
    async getBusiness(businessId) {
      const token = await this.$auth.getTokenSilently();
      BusinessService.getBusiness(businessId, token)
        .then((business) => {
          this.businesses.push(business);
          this.updateView();
        })
        .catch(this.reportError);
    },
    async updateBusiness() {
      if (this.businessChanged) {
        const token = await this.$auth.getTokenSilently();
        this.business.name = this.view.businessName;
        this.business.payrollSystem = this.view.payrollSystem;
        BusinessService.updateBusiness(this.business, token)
          .then(() => {
            this.reportUpdate();
          })
          .catch((err) => {
            if (err.message) {
              this.reportError(`Couldn't update business: ${err.message}`);
            } else {
              this.reportError("Couldn't update business");
            }
          });
      }
    },
    async saveCredentials(credentials) {
      this.saving = true;
      const token = await this.$auth.getTokenSilently();
      // Attach businessId to list of credentials and save through API
      const { businessId } = this.business;
      BusinessService.saveCredentials(
        businessId, { businessId, ...credentials }, token,
      )
        .then((response) => {
          this.reportUpdate('Successfully updated credentials');
          this.view.payrollCredentials = response.data;
          this.saving = false;
        })
        .catch((err) => {
          if (err.response) {
            const { data } = err.response;
            this.reportError(`Couldn't update credentials: ${data.message ? data.message : data}`);
          } else {
            this.reportError("Couldn't update credentials");
          }
          this.saving = false;
        });
    },
    async getOptions(type) {
      this.loadingOptions = true;
      try {
        const token = await this.$auth.getTokenSilently();
        const { businessId } = this.business;
        const options = await BusinessService.getPayrollOptions(businessId, token, type);
        this.payrollOptions[type] = options;
      } catch (err) {
        this.reportError(`Error fetching list of ${type}`);
      }
      this.loadingOptions = false;
    },
    reportUpdate(message) {
      this.popup.show = true;
      this.popup.message = message || 'Settings Updated';
      this.popup.colour = 'blue';
    },
    reportError(err) {
      this.popup.show = true;
      this.popup.colour = 'red';
      if (err.message) {
        this.popup.message = err.message;
      } else {
        this.popup.message = err;
      }
    },
  },
  computed: {
    businessChanged() {
      return (
        this.business.name !== this.view.businessName
        || this.business.payrollSystem !== this.view.payrollSystem
      );
    },
    business() {
      const selectedId = this.selectedBusinessId;
      const foundBusiness = this.businesses.find((business) => business.businessId === selectedId);
      if (foundBusiness.payrollCredentials === undefined) {
        foundBusiness.payrollCredentials = {};
      }
      return foundBusiness || {
        businessId: '',
        name: '',
        payrollSystem: '',
        payrollCredentials: {},
      };
    },
  },
  components: {
    KronosWFDSettings,
    KeypaySettings,
    KSCSettings,
    NimbusT2WSettings,
    XeroSettings,
  },
};
</script>

<style scoped>
div.container {
  /* max-width: 800px; */
  padding: 10px;
  margin: 0 auto;
}

</style>
